import _sortBy from 'lodash.sortby';
import { Edge } from 'cio/models/campaign';
import BaseActionAbstract from 'cio/app-models/action-abstracts/base';
import { action, computed, set, setProperties } from '@ember/object';
import { alias } from '@ember/object/computed';
import { task } from 'ember-concurrency';
import { differentFilter } from 'cio/utils/filters';
import { isEmpty, isNone } from '@ember/utils';
import isEqual from 'lodash.isequal';
import { deserializeFilter } from 'cio/utils/conditions/deserialize/attribute';
import { serializeFilter } from 'cio/utils/conditions/serialize/attribute';
import { serializeFilter as serializeFilterFromDeviceAttribute } from 'cio/utils/conditions/serialize/device-attribute';
import { edgesEndingInExitCount, retrieveOutgoingEdges } from 'cio/utils/visual-workflow-builder/branching';
import AttributeCondition from '../conditions/attribute';
import EventAttributeCondition from '../conditions/event-attribute';
import { SerializedLegacyFilter, SerializedLegacySegmentNegatableFilter } from 'cio/types/conditions/legacy-filter';
import AbstractCondition from '../conditions/abstract';
import DeviceAttributeCondition, {
  getSpecialDeviceAttributeConfig,
  isDeviceExistsCondition,
  isSpecialDeviceAttribute,
} from '../conditions/device-attribute';

const EDITABLE_PROPERTIES: string[] = ['name'];
const DIRTY_DEPENDENCIES = `action.{${EDITABLE_PROPERTIES.join(',')}}`;

const EDGE_EDITABLE_PROPERTIES: string[] = ['from', 'to', 'type', 'index'];
const EDGE_DIRTY_DEPENDENCIES = `edge.{${EDGE_EDITABLE_PROPERTIES.join(',')}}`;

class SegmentConditionModel {
  segments: (string | number)[];
  _originalSegments: (string | number)[];

  constructor(segments: (string | number)[] | null) {
    if (segments) {
      this.segments = segments;
      this._originalSegments = [...segments];
    } else {
      this.segments = [];
      this._originalSegments = [];
    }
  }

  @computed('_originalSegments.[]', 'segments.[]')
  get isDirty(): boolean {
    return !isEqual(this._originalSegments, this.segments);
  }

  @computed('segments.[]')
  get hasValue(): boolean {
    return !isEmpty(this.segments);
  }
}

export class EdgeAbstract {
  condition: any;
  edge: any;
  branchReconnectPoint: any;

  _originalCondition: any;

  conditionModel: any;
  conditionType!: string | null;

  from: any;
  to: any;
  type: any;
  index!: number;

  isNew = false;

  constructor(condition: any, edge: any, branchReconnectPoint: any, isNew = false) {
    setProperties(this, {
      edge,
      branchReconnectPoint,
      from: edge.from,
      to: edge.to,
      type: edge.type,
      index: edge.index,
      _originalCondition: isNone(condition) ? null : { ...condition },
      isNew,
    });

    this.setConditionModel(condition);
  }

  setConditionModel(condition: SerializedLegacyFilter): void {
    if (isNone(condition)) {
      setProperties(this, {
        conditionModel: {},
        conditionType: null,
      });
      return;
    }

    switch (condition.type) {
      case 'attribute': {
        let conditionModel = deserializeFilter(condition) as AttributeCondition;
        conditionModel.set('shouldValidate', false);
        setProperties(this, {
          conditionModel,
          conditionType: condition.type,
        });
        break;
      }

      case 'device_attribute': {
        let conditionModel = deserializeFilter(condition) as DeviceAttributeCondition;
        conditionModel.set('shouldValidate', false);
        setProperties(this, {
          conditionModel,
          conditionType: condition.type,
        });
        break;
      }

      case 'event_attribute': {
        let conditionModel = deserializeFilter(condition) as EventAttributeCondition;
        conditionModel.set('shouldValidate', false);
        setProperties(this, {
          conditionModel,
          conditionType: condition.type,
        });
        break;
      }

      case 'segments':
      case 'segments_negatable': {
        setProperties(this, {
          conditionModel: new SegmentConditionModel((condition as SerializedLegacySegmentNegatableFilter).segments),
          conditionType: condition.type,
        });
        break;
      }
    }
  }

  @computed(...EDGE_EDITABLE_PROPERTIES.concat(EDGE_DIRTY_DEPENDENCIES), 'conditionModel.{isDirty,hasValue}')
  get isDirty(): boolean {
    let { edge } = this;

    return (
      // @ts-ignore
      EDGE_EDITABLE_PROPERTIES.any((p: string) => this[p] !== edge[p]) || this.conditionModel?.isDirty
    );
  }
  @computed('to', 'branchReconnectPoint', 'isNew')
  get isPathEmpty(): boolean {
    if (this.isNew) {
      return true;
    }

    if (!this.branchReconnectPoint) {
      return isNone(this.to);
    }

    return this.to === this.branchReconnectPoint;
  }

  @alias('conditionModel.hasValue') hasValue!: boolean;

  @computed('conditionType', 'conditionModel.hasValue')
  get errors(): any | null {
    if (
      (this.conditionType === 'segments' || this.conditionType === 'segments_negatable') &&
      !this.conditionModel.hasValue
    ) {
      return {
        segments: ['Please select a segment'],
      };
    }

    return null;
  }
}

export default class MultiSplitAbstract extends BaseActionAbstract {
  type = 'multi_split';

  action!: any;
  actionType!: any;

  preconditions = [];
  multiConditions!: any;

  condition!: any;
  _originalCondition!: any;

  @computed('condition.{type,field}')
  get conditionModel() {
    let { condition } = this;
    let conditionModel: any = null;

    if (condition.type === 'attribute') {
      conditionModel = deserializeFilter(condition) as AttributeCondition;
    }
    if (condition.type === 'device_attribute') {
      conditionModel = deserializeFilter(condition) as DeviceAttributeCondition;
    }
    if (condition.type === 'event_attribute') {
      conditionModel = deserializeFilter(condition) as EventAttributeCondition;
    }
    if (condition.type === 'segments' || condition.type === 'segments_negatable') {
      conditionModel = new SegmentConditionModel((condition as SerializedLegacySegmentNegatableFilter).segments);
    }

    if (conditionModel != null) {
      conditionModel.set('shouldValidate', false);
    }

    let campaign = this.action.belongsTo('campaign').value();
    if (campaign?.event != null && campaign?.event !== '') {
      conditionModel.set('event', campaign.event);
    }

    return conditionModel;
  }

  _editableProperties: string[] = EDITABLE_PROPERTIES;
  _dirtyDependencies: string = DIRTY_DEPENDENCIES;

  originalEdges: any;
  edgeAbstracts!: EdgeAbstract[];
  name: any;
  edges: any;
  everyoneElseEdge: any;

  branchReconnectPoint: any;

  edgesToDelete: any;

  canDelete = false;
  canDeleteNotice: any;

  isOldVWB = false;

  prepareForEdit() {
    super.prepareForEdit();

    let actionEdges = _sortBy(
      this.action.campaign.edges.filter((edge: any) => edge.from === this.action.id),
      ['index'],
    );
    let everyoneElseEdge = actionEdges.pop();

    let branchReconnectPoint = this.action.campaign.branchRejoinPointsMap[this.action.id];

    let condition = { type: null };

    if (actionEdges.length > 0) {
      let [firstFilter] = this.action.multiConditions;
      let [firstConditionInFilter] = firstFilter;
      if (firstConditionInFilter) {
        let conditionCopy = { ...firstConditionInFilter };
        delete conditionCopy.value;
        delete conditionCopy.value2;
        delete conditionCopy.inverse;
        delete conditionCopy.segments;
        delete conditionCopy.negate;
        condition = conditionCopy;
      }
    }

    let canDelete = true;
    let canDeleteNotice = null;

    if (this.isOldVWB) {
      let branchReconnectPoint = this.action.campaign.branchRejoinPointsMap[this.action.id];
      let { traversedNodes, leafNodes } = retrieveOutgoingEdges(this.action, {
        edges: this.action.campaign.edges,
        reconnectNodeId: branchReconnectPoint,
      });
      // Traversed nodes includes the branch node we're traversing, so we're
      // checking for length of 1
      let isBranchEmpty = traversedNodes.length === 1 && leafNodes.length === 0;

      if (!isBranchEmpty) {
        canDelete = false;

        // Traversed nodes also include the leaf nodes.
        // If traversed nodes is 3 (this branch plus two exits), and there are two
        // exits, let's prompt to reconnect the exits before deleting.
        // Ideally we should be able to handle deleting a branch that only has
        // exits, but that requires jumping through extra hoops, so let's go with
        // this for now.
        if (traversedNodes.length === 3 && leafNodes.length === 2) {
          canDeleteNotice = 'Please reconnect all exits before deleting';
        } else {
          canDeleteNotice = 'Please remove all actions from within this branch before deleting';
        }
      }
    } else {
      let treeNode = this.action.campaign.branchesMap[this.action.id];
      canDelete = edgesEndingInExitCount(treeNode) !== treeNode.edges.length;

      if (!canDelete) {
        // If all edges of this branch action end in exts, prompt to reconnect the
        // exits before deleting.
        // Ideally we should be able to handle deleting a branch that only has
        // exits, but that requires jumping through extra hoops, so let's go with
        // this for now.
        canDeleteNotice = 'Please reconnect all exits before deleting';
      }
    }

    this.setProperties({
      originalEdges: actionEdges,
      edgeAbstracts: this.action.multiConditions.map((filter: any, index: number) => {
        let [edgeCondition] = filter;
        if (edgeCondition?.type === 'event_attribute') {
          edgeCondition.event = this.action.campaign.event;
        }
        return new EdgeAbstract(edgeCondition, actionEdges[index], branchReconnectPoint);
      }),
      everyoneElseEdge,
      branchReconnectPoint,
      edges: actionEdges,
      condition,
      canDelete,
      canDeleteNotice,
      _originalCondition: { ...condition },
      edgesToDelete: [],
    });
  }

  prepareForSave() {
    let action: any = super.prepareForSave();

    let baseCondition = this.condition;
    let newMultiConditions = this.edgeAbstracts.map((edgeAbstract) => {
      if (isNone(edgeAbstract.conditionType)) {
        return [];
      }
      let condition = { ...baseCondition };
      if (baseCondition.type === 'segments' || baseCondition.type === 'segments_negatable') {
        condition.segments = edgeAbstract.conditionModel.segments;
      } else {
        let filter =
          baseCondition.type === 'device_attribute'
            ? serializeFilterFromDeviceAttribute(edgeAbstract.conditionModel, { useInverse: false })
            : serializeFilter(edgeAbstract.conditionModel, false);
        condition = { ...condition, ...filter };
      }

      return [condition];
    });

    action.set('multiConditions', newMultiConditions);

    return action;
  }

  @computed(
    'originalEdges.[]',
    'edgeAbstracts.@each.isDirty',
    'condition.{type,field}',
    '_originalCondition.{type,field}',
  )
  // @ts-ignore - incompatible typings between mixin property and class getter
  get _isDirty(): boolean {
    return (
      this.originalEdges.length !== this.edgeAbstracts.length ||
      this.edgeAbstracts.some((edge) => edge.isDirty) ||
      differentFilter(this.condition, this._originalCondition) ||
      this.listOrderChanged()
    );
  }

  private listOrderChanged() {
    return (
      this.originalEdges.map((edge: Edge) => edge.index).toString() !==
      this.edgeAbstracts.map((edge: Edge) => edge.index).toString()
    );
  }

  @computed('condition.{type,field}', 'edgeAbstracts.@each.{isDirty,errors,hasValue}', 'originalEdges.[]')
  get errors() {
    // If the selected condition type is attribute or event attribute, but no
    // attribute was actually chosen, disable saving
    let requiredFieldTypes = new Map<string, string>([
      ['attribute', 'an attribute'],
      ['event_attribute', 'an event attribute'],
      ['device_attribute', 'a device attribute'],
    ]);
    let fieldIsRequiredButEmpty =
      this.condition && requiredFieldTypes.has(this.condition.type) && isEmpty(this.condition.field);
    if (fieldIsRequiredButEmpty) {
      let error = `Please select ${requiredFieldTypes.get(this.condition.type)}`;
      return {
        conditionField: [error],
      };
    }

    // If the selected condition type is attribute or event attribute and there isn't at least
    // one valid path, disable saving
    if (
      this.condition &&
      requiredFieldTypes.has(this.condition.type) &&
      !this.edgeAbstracts.find((edgeAbstract) => edgeAbstract.hasValue)
    ) {
      return {
        conditionField: ['Please add a value for at least one path'],
      };
    }

    // If the selected condition type is segments, but at least one path
    // doesn't have a segment selected, disable saving
    if (
      this.condition &&
      (this.condition.type === 'segments' || this.condition.type === 'segments_negatable') &&
      !this.edgeAbstracts.find((edgeAbstract) => edgeAbstract.hasValue)
    ) {
      return {
        conditions: ['Please select segments'],
      };
    }

    return;
  }

  saveTask = task({ drop: true }, async () => {
    let action = this.prepareForSave();

    let branchEdges: any[] = [];
    let otherEdges: any[] = [];
    this.action.campaign.consolidatedTree.edges.forEach((edge: any) => {
      if (edge.from === action.id) {
        branchEdges.push(edge);
      } else {
        otherEdges.push(edge);
      }
    });

    let hasNoReconnectPoint = this.edgeAbstracts.some((edge) => edge.to === -1);

    // If this branch's paths all end in individual exits (no reconnect point
    // available for the branch), we first connect all new paths to the first
    // action of the "All others" path and save that to the API. Then we
    // \issue another call to add individual exits for each of the new paths
    // and reconnect the edges to those.
    let newEdges = this.edgeAbstracts.map((edge: EdgeAbstract, index: number) => ({
      from: edge.from,
      to: edge.to === -1 ? this.everyoneElseEdge.to : edge.to,
      type: edge.type,
      index,
    }));

    let lastEdge = { ...this.everyoneElseEdge, index: newEdges.length };

    try {
      let response = await this.action.campaign.saveAction({ action, edges: [...otherEdges, ...newEdges, lastEdge] });

      if (hasNoReconnectPoint) {
        newEdges = [];
        let exitActions: any[] = [];
        this.edgeAbstracts.forEach((edge, index) => {
          let { to } = edge;
          if (edge.to === -1) {
            to = `${-(exitActions.length + 1)}`;
            exitActions.push({
              id: to,
              type: 'exit_action',
            });
          }

          newEdges.push({
            from: edge.from,
            to,
            type: edge.type,
            index,
          });
        });

        await this.action.campaign.addActions({
          actions: exitActions,
          edges: [...otherEdges, ...newEdges, lastEdge],
          copyFromEnv: false,
        });
      }

      this.prepareForEdit();
      return response;
    } catch (e) {
      action.rollbackAttributes();
      throw e;
    }
  });

  deleteTask = task({ drop: true }, async () => {
    return this.action.campaign.removeActionFromWorkflow(this.action);
  });

  addEdge() {
    let edge = {
      from: this.action.id,
      to: -1,
      type: 'branch',
      index: null,
    };

    if (this.originalEdges.length === 0) {
      edge.to = this.everyoneElseEdge.to;
    } else {
      if (!isNone(this.branchReconnectPoint)) {
        edge.to = this.branchReconnectPoint;
      }
    }

    let condition = { ...this.condition };

    if (['attribute', 'event_attribute', 'device_attribute'].includes(condition.type)) {
      condition.operator = 'eq';
    }

    this.edgeAbstracts.pushObject(new EdgeAbstract(condition, edge, this.branchReconnectPoint, true));
  }

  removeEdge(edgeAbstract: any) {
    this.edgesToDelete.pushObject(edgeAbstract);
    this.edgeAbstracts.removeObject(edgeAbstract);
  }

  @action
  setConditionField(field: string) {
    set(this.condition, 'field', field);

    this.edgeAbstracts.forEach((edgeAbstract, index) => {
      if (edgeAbstract.conditionModel) {
        let { type } = edgeAbstract.conditionModel as AbstractCondition;
        if (type === 'device_attribute') {
          this.updateDeviceConditionModel(edgeAbstract.conditionModel, field, index);
        } else {
          this.updateConditionModel(edgeAbstract.conditionModel, field);
        }
      }
    });
  }

  updateDeviceConditionModel(condition: DeviceAttributeCondition, name: string, index: number) {
    let operator = 'eq';
    let value = '';

    if (isSpecialDeviceAttribute(name)) {
      let { defaultOperator, defaultValue, values } = getSpecialDeviceAttributeConfig(name)!;
      value = defaultValue;
      operator = defaultOperator;
      if (isDeviceExistsCondition(name)) {
        operator = index === 0 ? 'exists' : '!exists';
      } else {
        value = index < values.length ? values[index].value : defaultValue;
      }
    }

    setProperties(condition.editableCondition, { name, operator, value });
    condition.editableCondition.applyUpdates?.();
  }

  updateConditionModel(condition: any, name: string) {
    set(condition.editableCondition, 'name', name);
    condition.editableCondition.applyUpdates?.();
  }

  selectConditionType(type: string) {
    let edgeCondition: any = { type };
    if (type === 'segments') {
      edgeCondition.segments = [];
    } else if (type === 'segments_negatable') {
      edgeCondition.segments = [];
      edgeCondition.negate = false;
    } else if (type === 'event_attribute') {
      edgeCondition.field = '';
      edgeCondition.event = this.action.campaign.isWebhookTriggered
        ? this.action.campaign.webhookId
        : this.action.campaign.event;
    } else {
      edgeCondition.field = '';
    }

    let currentConditionType = this.condition.type;
    let isCurrentlyAttributeType = ['attribute', 'event_attribute'].includes(currentConditionType);
    let isChangingToAttributeType = ['attribute', 'event_attribute'].includes(type);

    if (!isCurrentlyAttributeType && isChangingToAttributeType) {
      edgeCondition.operator = 'eq';
    }

    // create a condition object to set on the multi split action abstract when
    // changing condition types.
    // If the type is segments or segments_negatable, we don't want to clone
    // from "edgeCondition", because we don't need the "segments" array nor the
    // "negate" field. We only need the "type" to determine which path editors
    // to show.
    let condition: any = { type };
    if (!['segments', 'segments_negatable'].includes(type)) {
      condition = { ...edgeCondition };
    }

    this.set('condition', condition);
    this.edgeAbstracts.forEach((edgeAbstract: EdgeAbstract) => {
      let newCondition = { ...edgeCondition };

      // If we're switching from attribute to event_attribute (or the other
      // way), make sure to keep the current path value. If we're switching
      // from/to the segment type, reset the value.
      if (isCurrentlyAttributeType && isChangingToAttributeType) {
        newCondition.value = edgeAbstract.conditionModel.value;
        newCondition.value2 = edgeAbstract.conditionModel.value2;
        newCondition.operator = edgeAbstract.conditionModel.operator;
      }
      edgeAbstract.setConditionModel(newCondition);
    });
  }
}
